import React, { useState, useEffect } from "react";
import Avatar from "@mui/joy/Avatar";
import Box from "@mui/joy/Box";
import Chip from "@mui/joy/Chip";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import IconButton from "@mui/joy/IconButton";
import Typography from "@mui/joy/Typography";
import Menu from "@mui/joy/Menu";
import MenuButton from "@mui/joy/MenuButton";
import MenuItem from "@mui/joy/MenuItem";
import Dropdown from "@mui/joy/Dropdown";
import CircularProgress from "@mui/joy/CircularProgress";
import Snackbar from "@mui/joy/Snackbar";

import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { gql, useQuery, useMutation } from "@apollo/client";
import Pagination from "../components/Pagination";
import { usePagination } from "../components/usePagination";
import {
  useActionCable,
  useChannel,
} from "@aersoftware/react-use-action-cable";
import { getWebSocketUrl } from "../utils/actionCableUrl";

const OWNERSHIP_REQUESTS_QUERY = gql`
  query OwnershipRequests(
    $first: Int
    $after: String
    $last: Int
    $before: String
  ) {
    ownershipRequests(
      first: $first
      after: $after
      last: $last
      before: $before
    ) {
      edges {
        node {
          id
          state
          createdAt
          reviewedAt
          asset {
            field01
            field02
            field03
            issueNumber
            controlNumber
            plateNumber
            regNumber
            type
            make
            model
            description
            identificationNumber
            engineNumber
            expiryDate
          }
          requestor {
            firstName
            lastName
            email
          }
          reviewer {
            firstName
            lastName
            email
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

const REVIEW_OWNERSHIP_REQUEST_MUTATION = gql`
  mutation ReviewOwnershipRequest($input: ReviewOwnershipRequestInput!) {
    reviewOwnershipRequest(input: $input) {
      ownershipRequest {
        id
        state
        reviewedAt
        reviewer {
          firstName
          lastName
          email
        }
      }
    }
  }
`;

const RowMenu = ({ row }) => {
  const [reviewOwnershipRequest] = useMutation(
    REVIEW_OWNERSHIP_REQUEST_MUTATION
  );
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleError = (error) => {
    setErrorMessage(error?.message || "An error occurred");
    setOpen(true);
  };

  return (
    <>
      <Dropdown>
        <MenuButton
          slots={{ root: IconButton }}
          slotProps={{
            root: { variant: "plain", color: "neutral", size: "sm" },
          }}
        >
          <MoreHorizRoundedIcon />
        </MenuButton>
        <Menu size="sm" sx={{ minWidth: 140 }}>
          <MenuItem
            color="success"
            onClick={() =>
              reviewOwnershipRequest({
                variables: { input: { id: row.id, action: "ACCEPT" } },
                onError: handleError,
              })
            }
          >
            Approve
          </MenuItem>
          <MenuItem
            color="danger"
            onClick={() =>
              reviewOwnershipRequest({
                variables: { input: { id: row.id, action: "REJECT" } },
                onError: handleError,
              })
            }
          >
            Reject
          </MenuItem>
        </Menu>
      </Dropdown>
      <Snackbar
        open={open}
        color="danger"
        variant="soft"
        onClose={() => setOpen(false)}
        autoHideDuration={3000}
      >
        {errorMessage}
      </Snackbar>
    </>
  );
};

const Row = (props: { row: any; key?: any }) => {
  const { row } = props;
  const [open, setOpen] = useState(false);

  return (
    <React.Fragment>
      <tr>
        <td>
          <IconButton
            aria-label="expand row"
            variant="plain"
            color="neutral"
            size="sm"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </td>
        <td>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Avatar
              size="sm"
              alt={`${row.requestor.firstName} ${row.requestor.lastName}`}
            />
            <div>
              <Typography level="body-xs">
                {row.requestor.firstName} {row.requestor.lastName}
              </Typography>
              <Typography
                level="body-xs"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "200px",
                }}
              >
                {row.requestor.email}
              </Typography>
            </div>
          </Box>
        </td>
        <td>
          <Typography level="body-xs">
            {new Intl.DateTimeFormat("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }).format(new Date(row.createdAt))}
          </Typography>
        </td>
        <td>
          <Chip
            variant="soft"
            size="sm"
            color={
              {
                pending: "warning",
                accepted: "success",
                rejected: "danger",
              }[row.state] as ColorPaletteProp
            }
          >
            {row.state.toUpperCase()}
          </Chip>
        </td>
        <td>
          {row.reviewer && (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Avatar
                size="sm"
                alt={`${row.reviewer.firstName} ${row.reviewer.lastName}`}
              />
              <div>
                <Typography level="body-xs">
                  {row.reviewer.firstName} {row.reviewer.lastName}
                </Typography>
                <Typography
                  level="body-xs"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "200px",
                  }}
                >
                  {row.reviewer.email}
                </Typography>
              </div>
            </Box>
          )}
        </td>
        <td>
          {row.reviewedAt && (
            <Typography level="body-xs">
              {new Intl.DateTimeFormat("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              }).format(new Date(row.reviewedAt))}
            </Typography>
          )}
        </td>
        <td>
          {row.state === "pending" && (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <RowMenu row={row} />
            </Box>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5}>
            <Table size="sm">
              <tbody>
                <tr>
                  <td>
                    <Typography level="body-xs">Field 01</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{row.asset.field01}</Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Field 02</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{row.asset.field02}</Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Field 03</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{row.asset.field03}</Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Issue Number</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.asset.issueNumber}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Control Number</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.asset.controlNumber}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Plate Number</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.asset.plateNumber}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Reg Number</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.asset.regNumber}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Asset Type</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{row.asset.type}</Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Make</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{row.asset.make}</Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Model</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{row.asset.model}</Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Description</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.asset.description}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">
                      Identification Number
                    </Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.asset.identificationNumber}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Engine Number</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.asset.engineNumber}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Expiry Date</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {new Intl.DateTimeFormat("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      }).format(new Date(row.asset.expiryDate))}
                    </Typography>
                  </td>
                </tr>
              </tbody>
            </Table>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

const OwnershipRequestsTable = () => {
  const [pageSize] = useState(10);
  const { data, loading, error, fetchMore, refetch } = useQuery(
    OWNERSHIP_REQUESTS_QUERY,
    {
      variables: {
        first: pageSize,
      },
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    }
  );

  // Setup WebSocket connection
  const { actionCable } = useActionCable(getWebSocketUrl());
  const channel = useChannel(actionCable);

  useEffect(() => {
    channel.subscribe(
      { channel: "ApplicationChannel" },
      {
        received: (wsData) => {
          if (wsData.type === "ownership_request_updated") {
            refetch();
          }
        },
      }
    );

    return () => {
      channel.unsubscribe();
    };
  }, [refetch]);

  const {
    pageInfo,
    isLoadingMore,
    handlePageChange,
    canGoNext,
    canGoPrevious,
  } = usePagination({
    pageSize,
    data,
    fetchMore,
    getPageInfo: (data) => data.ownershipRequests.pageInfo,
    updateQuery: (prev, { fetchMoreResult }) => {
      if (!fetchMoreResult) return prev;
      return {
        ownershipRequests: {
          ...fetchMoreResult.ownershipRequests,
          edges: [...fetchMoreResult.ownershipRequests.edges],
        },
      };
    },
  });

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <Typography level="body-lg" color="danger">
          Error loading data: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          display: { xs: "none", sm: "initial" },
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "8px",
          }}
        >
          <thead>
            <tr>
              <th style={{ width: 40, padding: "12px 6px" }}></th>
              <th style={{ width: 240, padding: "12px 6px" }}>Requestor</th>
              <th style={{ width: 140, padding: "12px 6px" }}>Date</th>
              <th style={{ width: 140, padding: "12px 6px" }}>Status</th>
              <th style={{ width: 240, padding: "12px 6px" }}>Reviewer</th>
              <th style={{ width: 140, padding: "12px 6px" }}>Reviewed At</th>
              <th style={{ width: 40, padding: "12px 6px" }}></th>
            </tr>
          </thead>
          <tbody>
            {loading || isLoadingMore ? (
              <tr>
                <td colSpan={7}>
                  <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                    <CircularProgress size="sm" />
                  </Box>
                </td>
              </tr>
            ) : (
              data?.ownershipRequests?.edges?.map(({ node: row }) => (
                <Row key={row.id} row={row} />
              ))
            )}
          </tbody>
        </Table>
      </Sheet>
      <Pagination
        pageInfo={pageInfo}
        isLoadingMore={isLoadingMore}
        onPageChange={handlePageChange}
        canGoNext={canGoNext}
        canGoPrevious={canGoPrevious}
      />
    </React.Fragment>
  );
};

const OwnershipRequests = () => {
  return (
    <>
      <Box
        sx={{
          display: "flex",
          mb: 1,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "start", sm: "center" },
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Typography level="h2" component="h1">
          Ownership Requests
        </Typography>
      </Box>
      <OwnershipRequestsTable />
    </>
  );
};

export default OwnershipRequests;
